import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { BADGE_BASE_CLASSES, BadgeTone, TONE_CLASSES } from './Badge';
import { useAnchoredPosition } from './overlayPosition';

/**
 * `common/StatusChip` (M-A, architecture-ux-density-v2.md §5.1).
 *
 * A `Badge`-looking, interactive status token: a compact visible label (icon /
 * short text) that reveals a longer detail in an accessible tooltip/popover.
 * It replaces the long-text status pills in table cells across the app.
 *
 * The chip IS a Badge with interaction — it reuses `Badge`'s exact tone colour
 * language (`TONE_CLASSES`) and pill base classes rather than forking the hex
 * values (§5.1.5), so status colours stay one system app-wide.
 *
 * Behaviour contract (§5.1, bindend):
 * - Trigger is a native `<button type="button">` → Tab-focusable.
 * - Popover opens on hover (`mouseenter`), keyboard `focus`, and click/Enter/
 *   Space. A click "pins" it open (sticky) until another click / `Escape` /
 *   focus loss / outside click. Touch taps go through the click path (never
 *   hover-only).
 * - A11y: popover carries `role="tooltip"` + a `useId` id; the trigger carries
 *   `aria-describedby` (so a focused chip exposes `detail` to screen readers)
 *   and `aria-expanded` reflecting the pinned/click state. `Escape` closes and
 *   leaves focus on the trigger. `aria-describedby` resolves document-wide, so
 *   it keeps working across the portal boundary.
 * - Positioning (M-V4-A, architecture-ux-v4.md §1.4): the popover is rendered via
 *   `createPortal(document.body)` with `position: fixed` through the shared
 *   `useAnchoredPosition` primitive (preferred `bottom-start`). This escapes the
 *   clipping `overflow-x-auto` scrollport of `DataTable` (fixes D-V4-2 — the
 *   tooltip was previously an `absolute` child of the table subtree and got
 *   offset/hidden). Flip + clamp live in the primitive (verhaltens-identisch).
 * - `prefers-reduced-motion` is respected (motion-safe-gated fade only).
 */
export type StatusChipTone = BadgeTone;

export interface StatusChipProps {
  tone: StatusChipTone;
  /** Kurzform — sichtbares Token (Icon/Kurzlabel, i18n-übersetzt vom Aufrufer). */
  label: ReactNode;
  /** Langform — Inhalt des Tooltips/Popovers (i18n-übersetzt vom Aufrufer). */
  detail: ReactNode;
  /** Optionale Überschrift im Popover (z. B. Feldname „Prüfung"). */
  detailTitle?: ReactNode;
  /** Optionales Icon links vom Kurzlabel (aria-hidden). */
  icon?: ReactNode;
  /** Screenreader-Zusatz, falls `label` allein nicht selbsterklärend ist (z. B. reines Icon). */
  srLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function StatusChip({
  tone,
  label,
  detail,
  detailTitle,
  icon,
  srLabel,
  className = '',
  'data-testid': dataTestId,
}: StatusChipProps) {
  const popoverId = useId();
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  const { style: popoverStyle } = useAnchoredPosition(
    open,
    triggerRef,
    popoverRef,
    'bottom-start',
  );

  const close = useCallback(() => {
    setOpen(false);
    setPinned(false);
  }, []);

  const handleMouseEnter = useCallback(() => setOpen(true), []);

  // Leaving the chip only collapses a non-pinned popover — but NOT when the
  // pointer is moving onto the popover itself. Since the popover now lives in a
  // portal (outside this span's DOM subtree), we can't rely on the span's own
  // hover boundary; instead we inspect `relatedTarget`: if the pointer entered
  // the popover, keep it open (Bestandsverhalten — der Popover war früher ein
  // DOM-Kind).
  const handleContainerMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node && popoverRef.current?.contains(next)) return;
      setPinned((isPinned) => {
        if (!isPinned) setOpen(false);
        return isPinned;
      });
    },
    [],
  );

  const handlePopoverMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node && containerRef.current?.contains(next)) return;
      setPinned((isPinned) => {
        if (!isPinned) setOpen(false);
        return isPinned;
      });
    },
    [],
  );

  const handleFocus = useCallback(() => setOpen(true), []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLSpanElement>) => {
    // Only close when focus truly leaves the chip (popover content may be
    // interactive). relatedTarget === null (e.g. click-away) also closes. The
    // popover is portaled but is still a React child, so focus moving into it
    // bubbles here — check both the container AND the popover.
    const next = event.relatedTarget;
    if (next instanceof Node && (containerRef.current?.contains(next) || popoverRef.current?.contains(next))) return;
    close();
  }, [close]);

  const handleClick = useCallback(() => {
    // Toggle sticky pin. Click always follows a focus (which opened it), so on
    // the pinning click both stay open; the un-pinning click closes it.
    setPinned((prev) => {
      const nextPinned = !prev;
      setOpen(nextPinned);
      return nextPinned;
    });
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape' && (open || pinned)) {
      event.stopPropagation();
      close();
      triggerRef.current?.focus(); // keep focus on the trigger
    }
  }, [open, pinned, close]);

  // Outside-click dismissal while pinned. The popover is portaled, so a click
  // inside it is NOT inside the container — check both.
  useEffect(() => {
    if (!pinned) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [pinned, close]);

  return (
    <span
      ref={containerRef}
      className={['relative inline-flex', className].filter(Boolean).join(' ')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleContainerMouseLeave}
      onBlur={handleBlur}
      data-testid={dataTestId}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-describedby={popoverId}
        aria-expanded={pinned}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={[
          BADGE_BASE_CLASSES,
          TONE_CLASSES[tone],
          'cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-transparent',
        ].join(' ')}
      >
        {icon ? (
          <span className="shrink-0" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {label}
        {srLabel ? <span className="sr-only">{srLabel}</span> : null}
      </button>

      {open
        ? createPortal(
            <div
              ref={popoverRef}
              id={popoverId}
              role="tooltip"
              style={popoverStyle}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
              className={[
                'z-40 w-max max-w-xs',
                'rounded-card border border-default bg-surface-raised px-3 py-2',
                'text-sm text-secondary shadow-[var(--shadow-overlay)]',
                'motion-safe:animate-[fb-chip-pop-in_120ms_ease-out]',
              ].join(' ')}
            >
              {detailTitle ? (
                <div className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-muted">
                  {detailTitle}
                </div>
              ) : null}
              {detail}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

export default StatusChip;
